import React, { useState } from 'react';
import Header from '../../components/ui/Header';
import RegistrationForm from './components/RegistrationForm';
import SubscriptionTiers from './components/SubscriptionTiers';
import TrustSignals from './components/TrustSignals';
import SocialLogin from './components/SocialLogin';
import Icon from '../../components/AppIcon';

const UserRegistration = () => {
  const [selectedTier, setSelectedTier] = useState('premium');
  const [isLoading, setIsLoading] = useState(false);
  const [registrationStep, setRegistrationStep] = useState('form'); // 'form', 'verification', 'success'

  const handleRegistration = async (formData) => {
    setIsLoading(true);
    
    // Simulate API call
    try {
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Mock registration logic
      console.log('Registration data:', { ...formData, selectedTier });
      
      // Move to verification step
      setRegistrationStep('verification');
    } catch (error) {
      console.error('Registration failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSocialLogin = async (provider) => {
    setIsLoading(true);
    
    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      console.log('Social login with:', provider);
      
      // Redirect to dashboard after successful social login
      window.location.href = '/trading-dashboard';
    } catch (error) {
      console.error('Social login failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTierSelection = (tierId) => {
    setSelectedTier(tierId);
  };

  const VerificationStep = () => (
    <div className="w-full max-w-md mx-auto">
      <div className="bg-card border border-border rounded-lg p-8 text-center shadow-elevation-2">
        <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <Icon name="Mail" size={32} className="text-success" />
        </div>
        <h2 className="text-2xl font-semibold text-card-foreground mb-4">Check Your Email</h2>
        <p className="text-muted-foreground mb-6">
          We've sent a verification link to your email address. Please click the link to activate your account and complete the registration process.
        </p>
        <div className="space-y-4">
          <button
            onClick={() => setRegistrationStep('form')}
            className="text-primary hover:text-primary/80 font-medium transition-standard"
          >
            ← Back to Registration
          </button>
          <div className="text-sm text-muted-foreground">
            Didn't receive the email?{' '}
            <button className="text-primary hover:text-primary/80 font-medium transition-standard">
              Resend verification
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <Header 
        onLogin={() => {}}
        onLogout={() => {}}
        onUpgrade={() => {}}
      />
      <main className="pt-16">
        <div className="container mx-auto px-4 py-12">
          {registrationStep === 'form' && (
            <div className="space-y-12">
              {/* Hero Section */}
              <div className="text-center max-w-3xl mx-auto">
                <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
                  Start Your Crypto Trading Journey
                </h1>
                <p className="text-xl text-muted-foreground mb-8">
                  Join thousands of successful traders using AI-powered predictions to maximize their cryptocurrency investments
                </p>
                <div className="flex items-center justify-center space-x-8 text-sm text-muted-foreground">
                  <div className="flex items-center space-x-2">
                    <Icon name="TrendingUp" size={16} className="text-success" />
                    <span>94% Prediction Accuracy</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Icon name="Users" size={16} className="text-secondary" />
                    <span>50K+ Active Traders</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Icon name="Shield" size={16} className="text-primary" />
                    <span>Bank-Level Security</span>
                  </div>
                </div>
              </div>

              {/* Main Content Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 max-w-7xl mx-auto">
                {/* Left Column - Registration Form */}
                <div className="lg:col-span-1 space-y-8">
                  <RegistrationForm 
                    onSubmit={handleRegistration}
                    isLoading={isLoading}
                  />
                  
                  <SocialLogin 
                    onSocialLogin={handleSocialLogin}
                    isLoading={isLoading}
                  />
                </div>

                {/* Right Column - Subscription Tiers */}
                <div className="lg:col-span-2">
                  <SubscriptionTiers 
                    selectedTier={selectedTier}
                    onTierSelect={handleTierSelection}
                  />
                </div>
              </div>

              {/* Trust Signals Section */}
              <div className="max-w-4xl mx-auto">
                <TrustSignals />
              </div>

              {/* Additional Information */}
              <div className="bg-card border border-border rounded-lg p-8 max-w-4xl mx-auto">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <h3 className="text-lg font-semibold text-card-foreground mb-4">
                      Why Choose CryptoPredict Pro?
                    </h3>
                    <ul className="space-y-3">
                      <li className="flex items-start space-x-3">
                        <Icon name="Brain" size={20} className="text-primary mt-0.5 flex-shrink-0" />
                        <div>
                          <span className="font-medium text-card-foreground">Advanced AI Technology</span>
                          <p className="text-sm text-muted-foreground">
                            Our proprietary machine learning algorithms analyze market patterns 24/7
                          </p>
                        </div>
                      </li>
                      <li className="flex items-start space-x-3">
                        <Icon name="Zap" size={20} className="text-accent mt-0.5 flex-shrink-0" />
                        <div>
                          <span className="font-medium text-card-foreground">Real-Time Signals</span>
                          <p className="text-sm text-muted-foreground">
                            Get instant notifications for profitable trading opportunities
                          </p>
                        </div>
                      </li>
                      <li className="flex items-start space-x-3">
                        <Icon name="Shield" size={20} className="text-success mt-0.5 flex-shrink-0" />
                        <div>
                          <span className="font-medium text-card-foreground">Risk Management</span>
                          <p className="text-sm text-muted-foreground">
                            Built-in tools to help you manage and minimize trading risks
                          </p>
                        </div>
                      </li>
                    </ul>
                  </div>
                  
                  <div>
                    <h3 className="text-lg font-semibold text-card-foreground mb-4">
                      Getting Started is Easy
                    </h3>
                    <div className="space-y-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-medium">
                          1
                        </div>
                        <span className="text-card-foreground">Create your account in under 2 minutes</span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-secondary text-secondary-foreground rounded-full flex items-center justify-center text-sm font-medium">
                          2
                        </div>
                        <span className="text-card-foreground">Choose your subscription plan</span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-accent text-accent-foreground rounded-full flex items-center justify-center text-sm font-medium">
                          3
                        </div>
                        <span className="text-card-foreground">Start receiving AI-powered predictions</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {registrationStep === 'verification' && (
            <div className="flex items-center justify-center min-h-[60vh]">
              <VerificationStep />
            </div>
          )}
        </div>
      </main>
      {/* Footer */}
      <footer className="bg-card border-t border-border py-8">
        <div className="container mx-auto px-4">
          <div className="text-center text-sm text-muted-foreground">
            <p className="mb-2">
              © {new Date()?.getFullYear()} CryptoPredict Pro. All rights reserved.
            </p>
            <div className="flex items-center justify-center space-x-6">
              <button className="hover:text-foreground transition-standard">Terms of Service</button>
              <button className="hover:text-foreground transition-standard">Privacy Policy</button>
              <button className="hover:text-foreground transition-standard">Support</button>
              <button className="hover:text-foreground transition-standard">Contact</button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default UserRegistration;